<template>
    <div class="user-detail container-fluid">

        <!-- LOADING -->
        <div v-if="isLoading" class="loading-overlay">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Caricamento...</span>
            </div>
            <div class="loading-text">Caricamento in corso...</div>
        </div>

        <!-- CARD TITOLO -->
        <div class="card mb-2 shadow-sm">
            <div class="card-body d-flex align-items-center justify-content-between">
                <h5 class="mb-1"><i class="bi bi-person"></i> Il mio profilo</h5>
            </div>
        </div>

        <!-- ALERT ERRORI -->
        <div v-if="errors" class="alert alert-danger alert-dismissible fade show" role="alert">
            <ul class="mb-0">
                <li v-for="(msg, field) in errors" :key="field">{{ Array.isArray(msg) ? msg[0] : msg }}</li>
            </ul>
            <button type="button" class="btn-close" @click="errors = null"></button>
        </div>

        <!-- ALERT SUCCESSO -->
        <div v-if="successMessage" class="alert alert-success alert-dismissible fade show" role="alert">
            {{ successMessage }}
            <button type="button" class="btn-close" @click="successMessage = null"></button>
        </div>

        <!-- CARD DATI UTENTE -->
        <div class="card shadow-sm">
            <div class="card-body">
                <div v-if="user" class="row">

                    <!-- ETICHETTE -->
                    <div class="col-12 col-md-6 labels">
                        <ul class="list-unstyled">
                            <li>Nome</li>
                            <li>Cognome</li>
                            <li>Codice fiscale</li>
                            <li>Nome dell'Ente</li>
                            <li>Codice fiscale dell'Ente</li>
                            <li>Regione/Provincia autonoma di riferimento</li>
                            <li>Email istituzionale (NO Pec)</li>
                            <li>Recapito telefonico dell'ufficio</li>
                            <li v-if="user.privileges?.length">Permessi</li>
                        </ul>
                    </div>

                    <!-- VALORI -->
                    <div class="col-12 col-md-6 values">
                        <ul class="list-unstyled">
                            <li>{{ user.name || '—' }}</li>
                            <li>{{ user.surname || '—' }}</li>
                            <li>{{ user.fiscalcode || '—' }}</li>
                            <li>{{ user.organization || '—' }}</li>
                            <li>{{ user.organization_fiscal_code || '—' }}</li>
                            <li>{{ user.region_name || '—' }}</li>
                            <li>{{ user.email || '—' }}</li>
                            <li>{{ user.phone || '—' }}</li>
                            <li v-if="user.privileges?.length">
                                <div class="d-flex flex-wrap gap-2">
                                    <span
                                        v-for="p in user.privileges"
                                        :key="p.id"
                                        class="custom-badge"
                                    >
                                        {{ p.description }}
                                    </span>
                                </div>
                            </li>
                        </ul>
                    </div>

                </div>
                <div v-else-if="!isLoading">
                    Nessun dato disponibile.
                </div>
            </div>
        </div>

        <!-- CARD 2FA -->
        <div v-if="$root.user && $root.user.two_factor_editable" class="card mt-3 shadow-sm">
            <div class="card-header">
                <h5 class="card-title mb-0">Autenticazione a due fattori</h5>
            </div>
            <div class="card-body">
                <input
                    type="checkbox"
                    :checked="$root.user.two_factor_enabled"
                    @click.prevent="updateTwoFactor"
                    class="form-check-input me-2"
                    id="twoFactorEnabled"
                >
                <label class="form-check-label" for="twoFactorEnabled">
                    Autenticazione a due fattori
                </label>
            </div>
        </div>

        <!-- MODALE 2FA - ABILITA -->
        <ModalView
            v-model="show2FAModal"
            title="Autenticazione a due fattori"
            icon="lock"
            @update:modelValue="val => !val && close2FAModal()"
        >
            <div class="card">
                <div class="card-body row">
                    <div class="col-md-12" v-if="!twoFA.url">
                        <label for="password" class="form-label">Conferma la tua password</label>
                        <div class="input-group position-relative">
                            <input
                                :type="twoFA.showPassword ? 'text' : 'password'"
                                v-model="twoFA.password"
                                class="form-control"
                            >
                            <button
                                type="button"
                                class="btn btn-link position-absolute end-0 top-50 translate-middle-y text-decoration-none"
                                style="z-index: 5"
                                @click="twoFA.showPassword = !twoFA.showPassword"
                            >
                                <i class="bi" :class="twoFA.showPassword ? 'bi-eye-slash' : 'bi-eye'"></i>
                            </button>
                        </div>
                        <button type="button" @click="generateKeyImage()" class="btn btn-primary mt-2" :disabled="twoFA.loading">
                            <span v-if="twoFA.loading" class="spinner-border spinner-border-sm me-1"></span>
                            Genera
                        </button>
                    </div>
                    <div class="col-md-12" v-if="twoFA.url">
                        <div class="w-100 d-flex justify-content-center">
                            <div class="d-flex flex-column justify-content-center align-items-center">
                                <qrcode-vue :value="twoFA.url" :size="300" level="H" render-as="svg" class="p-4 bg-white rounded"/>
                                <span class="d-block mt-3">Token: <strong>{{ extractSecretFromUrl(twoFA.url) }}</strong></span>
                            </div>
                        </div>
                        <div class="form-group mt-3">
                            <label for="twoFACode" class="form-label">Inserisci il codice di verifica:</label>
                            <input
                                type="text"
                                id="twoFACode"
                                v-model="twoFA.confirm"
                                class="form-control"
                                placeholder="Codice a 6 cifre"
                                maxlength="6"
                            >
                        </div>
                        <button type="button" class="btn btn-primary mt-2" @click="send2FAConfirm">Conferma</button>
                    </div>
                </div>
            </div>
        </ModalView>

        <!-- MODALE 2FA - DISABILITA -->
        <ModalView
            v-model="show2FAModalRemove"
            title="Disabilita Autenticazione a due fattori"
            icon="lock"
            @update:modelValue="val => !val && close2FAModal()"
        >
            <div class="card">
                <div class="card-body row">
                    <div class="col-md-12">
                        <div class="form-group">
                            <label for="twoFACodeRemove" class="form-label">Inserisci il codice di verifica:</label>
                            <input
                                type="text"
                                id="twoFACodeRemove"
                                v-model="twoFA.confirm"
                                class="form-control"
                                placeholder="Codice a 6 cifre"
                                maxlength="6"
                            >
                            <button type="button" class="btn btn-danger mt-2" @click="send2FADisable">Disabilita</button>
                        </div>
                    </div>
                </div>
            </div>
        </ModalView>

    </div>
</template>

<script>
import ModalView from '../common/ModalView.vue';
import QrcodeVue from 'qrcode.vue';

export default {
    name: 'ProfileView',
    components: {
        ModalView,
        QrcodeVue,
    },
    data() {
        return {
            user: null,
            isLoading: true,
            errors: null,
            successMessage: null,
            show2FAModal: false,
            show2FAModalRemove: false,
            twoFA: {
                loading: false,
                showPassword: false,
                password: '',
                url: '',
                confirm: '',
            }
        }
    },
    async mounted() {
        try {
            this.isLoading = true;
            const response = await this.$root.$api.get(`/user-detail/${this.$root.user.id}`);
            this.user = response.data;
        } catch (error) {
            console.error('Errore durante il caricamento del profilo:', error);
        } finally {
            this.isLoading = false;
        }
    },
    methods: {
        async generateKeyImage() {
            try {
                this.twoFA.loading = true;
                this.errors = null;
                const response = await this.$root.$api.post('/auth/2fa/generate', { password: this.twoFA.password });
                this.twoFA.url = response.data.data;
            } catch (error) {
                this.errors = { general: [error.response?.data?.message || 'Errore durante la generazione del QR code.'] };
            } finally {
                this.twoFA.loading = false;
            }
        },
        async send2FAConfirm() {
            try {
                this.errors = null;
                if (this.twoFA.url) {
                    const response = await this.$root.$api.post('/auth/2fa/confirm', { code: this.twoFA.confirm });
                    if (response.data.access_token) {
                        localStorage.setItem('token', response.data.access_token);
                    }
                    this.$root.user.two_factor_enabled = true;
                    this.successMessage = 'Autenticazione a due fattori abilitata con successo.';
                    this.close2FAModal();
                }
            } catch (error) {
                this.errors = { general: [error.response?.data?.message || 'Codice non valido. Riprova.'] };
            }
        },
        async send2FADisable() {
            try {
                this.errors = null;
                await this.$root.$api.post('/auth/2fa/delete', { code: this.twoFA.confirm });
                this.$root.user.two_factor_enabled = false;
                this.successMessage = 'Autenticazione a due fattori disabilitata con successo.';
                this.close2FAModal();
            } catch (error) {
                this.errors = { general: [error.response?.data?.message || 'Codice non valido. Riprova.'] };
            }
        },
        updateTwoFactor() {
            if (this.$root.user?.two_factor_enabled) {
                this.show2FAModalRemove = true;
            } else {
                this.show2FAModal = true;
            }
        },
        close2FAModal() {
            this.show2FAModal = false;
            this.show2FAModalRemove = false;
            this.twoFA.loading = false;
            this.twoFA.showPassword = false;
            this.twoFA.password = '';
            this.twoFA.url = '';
            this.twoFA.confirm = '';
        },
        extractSecretFromUrl(url) {
            if (!url) return '';
            try {
                const secretMatch = url.match(/[?&]secret=([^&]+)/);
                return secretMatch?.[1] || '';
            } catch {
                return '';
            }
        }
    }
}
</script>

<style scoped>
.user-detail {
    background-color: #ffffff;
    border-radius: 12px;
    padding: 2rem;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
}

.labels li,
.values li {
    padding: 0.6rem 0;
    border-bottom: 1px solid #eee;
}

.labels li:last-child,
.values li:last-child {
    border-bottom: none;
}

.labels li {
    font-weight: 600;
    color: #444;
}

.values li {
    color: #222;
    word-break: break-word;
}

.custom-badge {
    display: inline-block;
    padding: 0.4rem 0.7rem;
    border: 1px solid #0b3d91;
    color: #0b3d91;
    background-color: #ffffff;
    border-radius: 20px;
    font-size: 0.85rem;
    font-weight: 500;
}

.loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    z-index: 2000;
}

.loading-text {
    margin-top: 1rem;
    font-weight: bold;
    color: #0d6efd;
}
</style>
